import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Box,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import useDrivePicker from "react-google-drive-picker";
import { useGoogleLogin } from "@react-oauth/google";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useTypedSelector } from "../../hooks.tsx";
import { clickedExampleActions } from "../../store/clickedExample.tsx";
import axios from "../../utils/axios.tsx";

interface ExampleItemsProps {
  children?: React.ReactNode;
}

interface ViewProps {
  exampleItems: string[];
  awaitingAskResponse: boolean;
  handleOnClick: React.MouseEventHandler<HTMLButtonElement>;
}

const MobileView: React.FC<ViewProps> = ({
  exampleItems,
  awaitingAskResponse,
  handleOnClick,
}) => {
  // State for active slide
  const [activeSlide, setActiveSlide] = useState(0);

  // Handle next slide
  const handleNext = () => {
    setActiveSlide((prev) => Math.min(prev + 1, exampleItems.length - 1));
  };

  // Handle previous slide
  const handlePrevious = () => {
    setActiveSlide((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Previous Button */}
      <IconButton onClick={handlePrevious} disabled={activeSlide === 0}>
        <ChevronLeft />
      </IconButton>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "300px",
        }}
      >
        <Button
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: "25px",
            whiteSpace: "nowrap",
            fontWeight: "bold",
            fontSize: {
              xs: "0.8rem",
              sm: "0.8rem",
              md: "0.9rem",
              lg: "1rem",
              xl: "1rem",
            },
            padding: {
              lg: "6px 16px",
              md: "5px 14px",
              sm: "4px 12px",
              xs: "3px 10px",
            },
          }}
          variant="outlined"
          onClick={handleOnClick}
          disabled={awaitingAskResponse}
        >
          {exampleItems[activeSlide]}
        </Button>
      </Box>

      <IconButton
        onClick={handleNext}
        disabled={activeSlide === exampleItems.length - 1}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );
};

const DesktopView: React.FC<ViewProps> = ({
  exampleItems,
  awaitingAskResponse,
  handleOnClick,
}) => {
  return (
    <Grid container spacing={1.2} justifyContent="center" direction="row">
      {exampleItems.map((example: string) => (
        <Grid
          key={example}
          sx={{
            m: { sm: "0% 0.5% 0% 0%" },
            maxWidth: "100%",
          }}
        >
          <Button
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "25px",
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: {
                xs: "0.8rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1rem",
                xl: "1rem",
              },
            }}
            variant="outlined"
            onClick={handleOnClick}
            disabled={awaitingAskResponse}
          >
            {example}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
};

const ExampleItems: React.FC<ExampleItemsProps> = () => {
  const dispatch = useDispatch();
  const [openPicker] = useDrivePicker();
  const exampleItems = useTypedSelector(
    (state) => state.exampleState.exampleItems,
  );
  const awaitingAskResponse = useTypedSelector(
    (state) => state.searchFieldState.awaitingAskResponse,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true for xs and sm screens

  const handleOpenPicker = useGoogleLogin({
    flow: "auth-code",
    scope:
      "https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file",
    onSuccess: async ({ code }: any) => {
      // Send code to backend to exchange for access_token & refresh_token
      const res = await axios.post("/v1/exchange-google-code/", { code });
      const accessToken = res.data.access_token;

      openPicker({
        clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        developerKey: import.meta.env.VITE_GOOGLE_API_KEY,
        viewId: "SPREADSHEETS",
        token: accessToken,
        showUploadView: true,
        supportDrives: true,
        callbackFunction: async (data: any) => {
          if (data.action === "picked") {
            const file = data.docs[0];

            await axios
              .post(
                "/v1/read-google-sheet/",
                JSON.stringify({ file_id: file.id, access_token: accessToken }),
              )
              .then((response) => {
                console.log(response);
              });
          }
        },
      });
    },
    onError: () => console.error("Google Login Failed"),
  });

  // Handle click on example button
  const handleOnClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.currentTarget.textContent?.includes("Sheets")) {
      return handleOpenPicker();
    }
    let searchText = event.currentTarget.textContent;
    dispatch(clickedExampleActions.setText(searchText));
  };

  if (isMobile) {
    return (
      <MobileView
        exampleItems={exampleItems}
        awaitingAskResponse={awaitingAskResponse}
        handleOnClick={handleOnClick}
      />
    );
  } else {
    return (
      <DesktopView
        exampleItems={exampleItems}
        awaitingAskResponse={awaitingAskResponse}
        handleOnClick={handleOnClick}
      />
    );
  }
};

export default ExampleItems;
